import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin, getCurrentAccount } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Search, ChevronLeft, ChevronRight, X,
  ArrowUpDown, ArrowUp, ArrowDown, FileText, Building,
  AlertTriangle, Check, CreditCard, ArrowRightLeft,
  Loader2, Trash2, CheckCircle2, File, Lock, Plus, Pencil, BookOpen, ChevronsUpDown, Download, Home, DoorOpen,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => { if (!d) return '\u2014'; try { return format(new Date(d + 'T00:00:00'), 'MM/dd/yy'); } catch { return d; } };

const STATUS_COLORS = {
  paid: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20',
  unpaid: 'bg-amber-500/15 text-amber-600 border-amber-500/20',
  hold: 'bg-red-500/15 text-red-600 border-red-500/20',
};
const PF_STATUS_COLORS = {
  review: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-blue-100 text-blue-700 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const DUE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Due Dates' },
  { value: 'due_1', label: 'Due < 24h' },
  { value: 'due_3', label: 'Due < 3 days' },
  { value: 'due_5', label: 'Due < 5 days' },
  { value: 'due_7', label: 'Due < 7 days' },
  { value: 'overdue', label: 'Overdue (all)' },
  { value: 'overdue_3', label: 'Overdue > 3 days' },
  { value: 'overdue_5', label: 'Overdue > 5 days' },
  { value: 'overdue_7', label: 'Overdue > 7 days' },
];

function VendorCombobox({ vendors, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = vendors.filter(v => !search || v.toLowerCase().includes(search.toLowerCase()));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center justify-between h-8 w-44 text-xs border rounded px-2 bg-background hover:bg-muted/50 transition-colors truncate" data-testid="filter-vendor">
          <span className="truncate">{value || 'All Vendors'}</span>
          <ArrowUpDown className="h-3 w-3 ml-1 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 border-b">
          <Input placeholder="Search vendors..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-7 text-xs" data-testid="vendor-search-input" />
        </div>
        <div className="max-h-48 overflow-y-auto">
          <button onClick={() => { onChange(''); setOpen(false); setSearch(''); }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 ${!value ? 'font-semibold' : ''}`}>
            {!value && <Check className="h-3 w-3" />}<span className={!value ? '' : 'ml-5'}>All Vendors</span>
          </button>
          {filtered.map(v => (
            <button key={v} onClick={() => { onChange(v); setOpen(false); setSearch(''); }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 text-left ${value === v ? 'font-semibold' : ''}`}>
              {value === v && <Check className="h-3 w-3" />}<span className={value === v ? '' : 'ml-5'}>{v}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No vendors found</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OrgCombobox({ orgs, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = orgs.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()));
  const selectedName = orgs.find(o => o.id === value)?.name || 'All Orgs';
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center justify-between h-8 w-48 text-xs border rounded px-2 bg-background hover:bg-muted/50 transition-colors truncate" data-testid="filter-org">
          <Building className="h-3.5 w-3.5 mr-1 flex-shrink-0 opacity-60" />
          <span className="truncate flex-1 text-left">{selectedName}</span>
          <ChevronsUpDown className="h-3 w-3 ml-1 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 border-b">
          <Input placeholder="Search orgs..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-7 text-xs" autoFocus data-testid="org-search-input" />
        </div>
        <div className="max-h-48 overflow-y-auto">
          <button onClick={() => { onChange(''); setOpen(false); setSearch(''); }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 ${!value ? 'font-semibold' : ''}`}>
            {!value && <Check className="h-3 w-3" />}<span className={!value ? '' : 'ml-5'}>All Orgs</span>
          </button>
          {filtered.map(o => (
            <button key={o.id} onClick={() => { onChange(o.id); setOpen(false); setSearch(''); }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 text-left ${value === o.id ? 'font-semibold' : ''}`}>
              {value === o.id && <Check className="h-3 w-3" />}<span className={value === o.id ? '' : 'ml-5'}>{o.name}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No orgs found</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PAGE_SIZE = 50;

export default function PaymentsBillsWorkspace({ isGlobal = false, noLayout = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentAccount = getCurrentAccount();
  const { user } = getAuth();
  const isSuper = isSuperAdmin();
  const effectiveGlobal = isGlobal && isSuper;
  const userId = user?.id || user?.user_id || '';
  const userName = user?.name || '';

  // Feature visibility
  const [featureVisibility, setFeatureVisibility] = useState({});
  useEffect(() => {
    if (!currentAccount?.id || !userId) return;
    api.get(`/accounts/${currentAccount.id}/users/${userId}/feature-visibility`)
      .then(({ data }) => setFeatureVisibility(data.feature_visibility || {}))
      .catch(() => {});
  }, [currentAccount?.id, userId]);
  const showWarnings = featureVisibility.pf_validation_warnings === true;

  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterOptions, setFilterOptions] = useState(null);

  // Filters from URL
  const sortBy = searchParams.get('sort_by') || 'due_date';
  const sortDir = searchParams.get('sort_dir') || 'asc';
  const filterOrg = searchParams.get('org') || '';
  const filterVendor = searchParams.get('vendor') || '';
  const filterType = searchParams.get('utility_type') || '';
  const filterPayStatus = searchParams.get('payment_status') || '';
  const filterRefund = searchParams.get('refund_status') || '';
  const filterPfStatus = searchParams.get('pf_status') || '';
  const filterAccount = searchParams.get('account_number') || '';
  const filterDue = searchParams.get('due_filter') || '';
  const dueWithinDays = searchParams.get('due_within_days') || '';
  const filterPaidBy = searchParams.get('paid_by') || '';
  const filterTxnCount = searchParams.get('txn_filter') || '';
  const filterProperty = searchParams.get('property_id') || '';
  const filterBillType = searchParams.get('bill_type') || '';
  const [searchInput, setSearchInput] = useState(filterAccount);
  const accountInputRef = useRef(null);
  const shouldRefocusAccount = useRef(false);

  const accountId = effectiveGlobal ? (filterOrg || undefined) : currentAccount?.id;

  // Invoice drawer
  const [invoiceBill, setInvoiceBill] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfMessage, setPdfMessage] = useState(null);

  // Payment drawer
  const [txnDrawerOpen, setTxnDrawerOpen] = useState(false);
  const [txnBill, setTxnBill] = useState(null);
  const [txnForm, setTxnForm] = useState({});
  const [txnSaving, setTxnSaving] = useState(false);
  const [billTxns, setBillTxns] = useState([]);
  const [billTxnsLoading, setBillTxnsLoading] = useState(false);
  const [showAddTxnForm, setShowAddTxnForm] = useState(false);
  const [deletingTxnId, setDeletingTxnId] = useState(null);
  const [editingTxnId, setEditingTxnId] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [savingSelection, setSavingSelection] = useState(false);

  // Refund drawer
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [refundBill, setRefundBill] = useState(null);
  const [refundForm, setRefundForm] = useState({});
  const [refundSaving, setRefundSaving] = useState(false);

  // Org users for "Paid By" selector
  const [orgUsers, setOrgUsers] = useState([]);
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);

  // Bill locks - prevents concurrent edits
  const [billLocks, setBillLocks] = useState({}); // bill_id → {user_name, lock_type}

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const params = {};
        if (!effectiveGlobal && currentAccount?.id) params.account_id = currentAccount.id;
        const { data } = await api.get('/payments/v2/workspace/filter-options', { params });
        setFilterOptions(data);
      } catch (err) { console.error('Failed to load filter options', err); }
    };
    loadFilters();
    // Load org users for Paid By selector
    const acctId = currentAccount?.id;
    if (acctId) {
      api.get(`/accounts/${acctId}/users-list`).then(({ data }) => {
        setOrgUsers(Array.isArray(data) ? data : []);
      }).catch(() => {});
    }
  }, [effectiveGlobal, currentAccount?.id]);

  // Build API params from URL
  const buildParams = useCallback((pg) => {
    const params = { page: pg, limit: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
    if (accountId) params.account_id = accountId;
    if (filterVendor) params.vendor = filterVendor;
    if (filterType) params.utility_type = filterType;
    if (filterPayStatus) params.payment_status = filterPayStatus;
    if (filterRefund) params.refund_status = filterRefund;
    if (filterPfStatus) params.pf_status = filterPfStatus;
    if (filterAccount) params.account_number = filterAccount;
    if (filterPaidBy) params.paid_by = filterPaidBy;
    if (filterTxnCount) params.txn_filter = filterTxnCount;
    if (filterProperty) params.property_id = filterProperty;
    if (filterBillType) params.bill_type = filterBillType;
    if (dueWithinDays) {
      params.due_within_days = dueWithinDays;
    } else if (filterDue) {
      if (filterDue.startsWith('due_')) params.due_within_days = filterDue.replace('due_', '');
      else if (filterDue === 'overdue') params.overdue_days = 0;
      else if (filterDue.startsWith('overdue_')) params.overdue_days = filterDue.replace('overdue_', '');
    }
    return params;
  }, [sortBy, sortDir, accountId, filterVendor, filterType, filterPayStatus, filterRefund, filterPfStatus, filterAccount, filterPaidBy, filterTxnCount, filterProperty, filterBillType, filterDue, dueWithinDays]);

  // Initial load
  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/payments/v2/workspace/bills', { params: buildParams(1) });
      setBills(data.bills);
      setTotal(data.total);
      setPage(1);
      setHasMore(data.bills.length >= PAGE_SIZE);
      // Fetch locks for visible bills
      if (data.bills.length > 0) {
        const ids = data.bills.map(b => b.bill_id).join(',');
        api.get('/payments/v2/workspace/bill-locks', { params: { bill_ids: ids } })
          .then(({ data: ld }) => setBillLocks(ld.locks || {}))
          .catch(() => {});
      }
    } catch (err) { toast.error('Failed to load bills'); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { loadBills(); }, [loadBills]);

  // Load more
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const { data } = await api.get('/payments/v2/workspace/bills', { params: buildParams(nextPage) });
      setBills(prev => [...prev, ...data.bills]);
      setPage(nextPage);
      setHasMore(data.bills.length >= PAGE_SIZE);
    } catch { /* silent */ }
    finally { setLoadingMore(false); }
  }, [page, hasMore, loadingMore, buildParams]);

  // URL param helpers
  const setParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) { p.set(key, value); } else { p.delete(key); }
    if (key === 'due_filter') p.delete('due_within_days');
    if (key === 'due_within_days') p.delete('due_filter');
    setSearchParams(p);
  };
  const clearAllFilters = () => { setSearchParams({}); setSearchInput(''); };
  const handleSort = (field) => {
    if (sortBy === field) { setParam('sort_dir', sortDir === 'asc' ? 'desc' : 'asc'); }
    else { const p = new URLSearchParams(searchParams); p.set('sort_by', field); p.set('sort_dir', 'asc'); setSearchParams(p); }
  };
  const handleAccountSearch = (e) => {
    e.preventDefault();
    shouldRefocusAccount.current = true;
    setParam('account_number', searchInput);
  };

  // Refocus account input after data finishes loading (works for both Go button and Enter key)
  useEffect(() => {
    if (!loading && shouldRefocusAccount.current) {
      shouldRefocusAccount.current = false;
      setTimeout(() => accountInputRef.current?.focus(), 50);
    }
  }, [loading]);

  const hasActiveFilters = filterOrg || filterVendor || filterType || filterPayStatus || filterRefund || filterPfStatus || filterAccount || filterDue || dueWithinDays || filterProperty || filterBillType;

  // ── Invoice PDF ──
  const openInvoice = async (bill) => {
    setInvoiceBill(bill);
    setPdfLoading(true); setPdfBase64(null); setPdfUrl(null); setPdfMessage(null);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${bill.bill_id}`, { params: { account_id: bill.account_id } });
      if (data.base64_data) setPdfBase64(data.base64_data);
      else if (data.pdf_url) setPdfUrl(data.pdf_url);
      else setPdfMessage(data.message || 'No PDF available for this bill');
    } catch { setPdfMessage('Failed to load PDF'); }
    finally { setPdfLoading(false); }
  };
  const pdfDataUrl = pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null;

  // ── CSV Export with streaming progress ──
  const handleDownloadCsv = async () => {
    const toastId = toast.loading('Preparing CSV export…');
    try {
      const params = { ...buildParams(1) };
      delete params.page; delete params.limit; delete params.sort_by; delete params.sort_dir;
      const qs = new URLSearchParams(
        Object.entries(params).reduce((acc, [k, v]) => { if (v !== undefined && v !== '') acc[k] = v; return acc; }, {})
      ).toString();
      const auth = getAuth();
      const token = auth?.token;
      const url = `${api.defaults.baseURL}/payments/v2/workspace/bills/export${qs ? `?${qs}` : ''}`;

      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok || !resp.body) {
        toast.dismiss(toastId);
        toast.error('CSV export failed');
        return;
      }

      // Stream chunks → count newlines (rows) as they arrive
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      const chunks = [];
      let rows = 0;
      let lastToast = Date.now();
      let partial = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        const text = decoder.decode(value, { stream: true });
        partial += text;
        const newlineCount = (partial.match(/\n/g) || []).length;
        partial = partial.slice(partial.lastIndexOf('\n') + 1);
        rows += newlineCount;
        if (Date.now() - lastToast > 200) {
          const dataRows = Math.max(0, rows - 1); // exclude header
          toast.loading(`Generating CSV… ${dataRows.toLocaleString()} rows streamed`, { id: toastId });
          lastToast = Date.now();
        }
      }

      const blob = new Blob(chunks, { type: 'text/csv' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `pf_bills_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dlUrl);
      const finalRows = Math.max(0, rows - 1);
      toast.success(`CSV downloaded · ${finalRows.toLocaleString()} row${finalRows === 1 ? '' : 's'}`, { id: toastId });
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('CSV export failed');
    }
  };

  // ── Payment drawer ──
  const openPaymentDrawer = async (bill) => {
    // Acquire lock first
    try {
      const { data: lockRes } = await api.post('/payments/v2/workspace/lock-bill', {
        bill_id: bill.bill_id, payment_file_id: bill.payment_file_id,
        user_id: userId, user_name: userName, lock_type: 'payment',
      });
      if (!lockRes.locked) {
        toast.error(lockRes.message || `Bill is being edited by ${lockRes.held_by}`);
        return;
      }
    } catch { /* continue without lock on error */ }

    const isPaid = (bill.paid_amount && parseFloat(bill.paid_amount) > 0) || bill.payment_status === 'paid';

    // Set ALL state before opening drawer (prevents flicker from stale render)
    const newForm = isPaid ? {
      transaction_reference: bill.transaction_reference || '',
      amount: bill.paid_amount || '',
      payment_service_charge: bill.payment_service_charge || '',
      payment_method: bill.transaction_type || bill.payment_method || '',
      notes: bill.notes || '',
      transaction_date: bill.transaction_date ? bill.transaction_date.split('T')[0] : '',
      _totalRequested: bill.payment_amount || bill.total_amount_due || 0,
      paid_by_name: bill.paid_by_name || '',
      _locked: true,
    } : {
      transaction_reference: '', amount: bill.payment_amount || bill.total_amount_due || '',
      payment_service_charge: '', payment_method: '', notes: '',
      transaction_date: new Date().toISOString().split('T')[0],
      _totalRequested: bill.payment_amount || bill.total_amount_due || 0,
      paid_by_id: userId, paid_by_name: userName,
      _locked: false,
    };

    setTxnBill(bill);
    setTxnForm(newForm);
    setEditingTxnId(null);
    setShowAddTxnForm(!isPaid);
    setBillTxns([]);
    setBillTxnsLoading(true);
    setTxnDrawerOpen(true);
    setPaymentSummary(null);

    // Fetch canonical summary + transaction history in parallel
    try {
      const [summaryRes, txnRes] = await Promise.all([
        api.get('/payments/v2/workspace/bill-payment-summary', {
          params: { bill_id: bill.bill_id, payment_file_id: bill.payment_file_id, account_id: bill.account_id },
        }).catch(() => ({ data: null })),
        api.get(`/payments/v2/bill-transactions/${bill.bill_id}`, { params: { account_id: bill.account_id } }),
      ]);
      setBillTxns(txnRes.data.transactions || []);
      setPaymentSummary(summaryRes.data || null);
    } catch { setBillTxns([]); }
    finally { setBillTxnsLoading(false); }
  };

  const closeTxnDrawer = () => {
    if (txnBill) {
      api.post('/payments/v2/workspace/unlock-bill', {
        bill_id: txnBill.bill_id, payment_file_id: txnBill.payment_file_id,
        user_id: userId, user_name: userName,
      }).catch(() => {});
    }
    setTxnDrawerOpen(false);
    setTxnBill(null);
    setBillTxns([]);
    setShowAddTxnForm(false);
    setPaymentSummary(null);
  };

  const saveTxn = async () => {
    if (!txnForm.transaction_reference?.trim()) { toast.error('Transaction reference required'); return; }
    if (!txnForm.amount || parseFloat(txnForm.amount) <= 0) { toast.error('Valid amount required'); return; }
    if (!txnBill?.payment_file_id) { toast.error('Missing payment file context'); return; }

    // Full-payment warning: refresh summary first, then confirm if already fully paid
    try {
      const { data: latest } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: txnBill.bill_id, payment_file_id: txnBill.payment_file_id, account_id: txnBill.account_id },
      });
      setPaymentSummary(latest);
      if (latest.is_fully_paid) {
        const ok = window.confirm(
          `This bill is already fully paid (paid ${(latest.txn_sum || 0).toFixed(2)} of ${(latest.payment_amount || 0).toFixed(2)} via ${latest.transaction_count} transaction(s)).\n\nAre you sure you want to record ANOTHER transaction?`
        );
        if (!ok) return;
      }
    } catch { /* continue on summary failure */ }

    setTxnSaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${txnBill.payment_file_id}/utilities`, {
        params: { account_id: txnBill.account_id, account_number: txnBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === txnBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record for this bill'); setTxnSaving(false); return; }

      await api.post(`/payments/v2/files/${txnBill.payment_file_id}/record-payment`, {
        account_id: txnBill.account_id,
        utility_id: matchUtil.payment_file_utility_id,
        transaction_reference: txnForm.transaction_reference,
        amount: parseFloat(txnForm.amount),
        payment_service_charge: parseFloat(txnForm.payment_service_charge || 0),
        payment_method: txnForm.payment_method || '',
        notes: txnForm.notes || '',
        paid_by: txnForm.paid_by_id || userId,
        paid_by_name: txnForm.paid_by_name || userName,
        created_by: userId,
        created_by_name: isSuper ? 'System' : userName,
        transaction_date: txnForm.transaction_date || '',
      });
      toast.success('Payment recorded');
      // Refresh bill txns list (stay in drawer)
      const { data } = await api.get(`/payments/v2/bill-transactions/${txnBill.bill_id}`, { params: { account_id: txnBill.account_id } });
      setBillTxns(data.transactions || []);
      const totalReq = parseFloat(txnBill.payment_amount || txnBill.total_amount_due || 0);
      const remaining = Math.max(0, totalReq - (data.total || 0));
      setTxnForm(prev => ({ ...prev, transaction_reference: '', amount: remaining > 0 ? remaining : '', payment_service_charge: '', notes: '', transaction_date: new Date().toISOString().split('T')[0] }));
      setShowAddTxnForm(false);
      loadBills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setTxnSaving(false); }
  };

  const clearTxn = async () => {
    if (!txnBill?.payment_file_id) return;
    setTxnSaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${txnBill.payment_file_id}/utilities`, {
        params: { account_id: txnBill.account_id, account_number: txnBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === txnBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record'); setTxnSaving(false); return; }

      await api.post(`/payments/v2/files/${txnBill.payment_file_id}/clear-payment`, {
        account_id: txnBill.account_id, utility_id: matchUtil.payment_file_utility_id,
        transaction_reference: '', amount: 0, paid_by: userId, paid_by_name: userName,
      });
      toast.success('Payment cleared');
      closeTxnDrawer();
      loadBills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear'); }
    finally { setTxnSaving(false); }
  };

  const deleteTxnEntry = async (txnId) => {
    setDeletingTxnId(txnId);
    try {
      await api.delete(`/payments/v2/delete-transaction/${txnId}`, { params: { account_id: txnBill?.account_id, user_id: userId || '', user_name: userName || '' } });
      toast.success('Transaction deleted');
      if (txnBill?.bill_id) {
        const { data } = await api.get(`/payments/v2/bill-transactions/${txnBill.bill_id}`, { params: { account_id: txnBill.account_id } });
        setBillTxns(data.transactions || []);
        const totalReq = parseFloat(txnBill.payment_amount || txnBill.total_amount_due || 0);
        const remaining = Math.max(0, totalReq - (data.total || 0));
        setTxnForm(prev => ({ ...prev, amount: remaining > 0 ? remaining : '' }));
        if ((data.transactions || []).length === 0) setShowAddTxnForm(true);
      }
      loadBills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setDeletingTxnId(null); }
  };


  // ── Refund drawer ──
  const openRefundDrawer = async (bill) => {
    // Acquire lock
    try {
      const { data: lockRes } = await api.post('/payments/v2/workspace/lock-bill', {
        bill_id: bill.bill_id, payment_file_id: bill.payment_file_id,
        user_id: userId, user_name: userName, lock_type: 'refund',
      });
      if (!lockRes.locked) {
        toast.error(lockRes.message || `Bill is being edited by ${lockRes.held_by}`);
        return;
      }
      if (lockRes.current_refund === 'yes' && !bill.refund_amount) {
        toast.warning(`Refund already recorded (${lockRes.current_refund_amount}). Opening in edit mode.`);
        bill = { ...bill, refund: 'yes', refund_amount: lockRes.current_refund_amount };
      }
    } catch { /* continue */ }

    const isEdit = bill.refund === 'yes';
    setRefundForm({
      refund_amount: isEdit ? bill.refund_amount : '',
      refunded_by: isEdit ? (bill.refunded_by || '') : '',
      refund_method: isEdit ? (bill.refund_method || '') : '',
      refund_date: isEdit ? (bill.refund_date || '') : '',
      isEdit,
    });
    setRefundBill(bill);
    setRefundDrawerOpen(true);
  };

  const closeRefundDrawer = () => {
    if (refundBill) {
      api.post('/payments/v2/workspace/unlock-bill', {
        bill_id: refundBill.bill_id, payment_file_id: refundBill.payment_file_id,
        user_id: userId, user_name: userName,
      }).catch(() => {});
    }
    setRefundDrawerOpen(false);
    setRefundBill(null);
  };

  const saveRefund = async () => {
    if (!refundForm.refund_amount || parseFloat(refundForm.refund_amount) <= 0) { toast.error('Valid refund amount required'); return; }
    if (!refundBill?.payment_file_id) { toast.error('Missing payment file context'); return; }
    setRefundSaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${refundBill.payment_file_id}/utilities`, {
        params: { account_id: refundBill.account_id, account_number: refundBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === refundBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record'); setRefundSaving(false); return; }

      await api.post(`/payments/v2/files/${refundBill.payment_file_id}/record-refund`, {
        account_id: refundBill.account_id, utility_id: matchUtil.payment_file_utility_id,
        refund_amount: parseFloat(refundForm.refund_amount),
        refunded_by: refundForm.refunded_by || userName || '3rd Party Payment',
        refund_method: refundForm.refund_method || '',
        refund_date: refundForm.refund_date || new Date().toISOString().split('T')[0],
        user_id: userId, user_name: userName,
        created_by: userId,
        created_by_name: isSuper ? 'System' : userName,
      });
      toast.success('Refund recorded');
      closeRefundDrawer();
      loadBills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record refund'); }
    finally { setRefundSaving(false); }
  };

  const clearRefund = async () => {
    if (!refundBill?.payment_file_id) return;
    setRefundSaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${refundBill.payment_file_id}/utilities`, {
        params: { account_id: refundBill.account_id, account_number: refundBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === refundBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record'); setRefundSaving(false); return; }

      await api.post(`/payments/v2/files/${refundBill.payment_file_id}/clear-refund`, {
        account_id: refundBill.account_id, utility_id: matchUtil.payment_file_utility_id,
        refund_amount: 0, user_id: userId, user_name: userName,
      });
      toast.success('Refund cleared');
      closeRefundDrawer();
      loadBills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear refund'); }
    finally { setRefundSaving(false); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const threeDays = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);

  const inner = (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4" data-testid="bills-workspace">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="workspace-title">
              {effectiveGlobal ? 'Global PF Bills Workspace' : 'PF Bills'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {total.toLocaleString()} bills{hasActiveFilters ? ' (filtered)' : ''}
              {bills.length < total && <span className="ml-1 text-blue-600">showing {bills.length}</span>}
            </p>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} data-testid="clear-all-filters">
              <X className="h-4 w-4 mr-1" /> Clear Filters
            </Button>
          )}
          <Button variant="outline" size="sm" className="text-xs h-8 ml-auto" onClick={handleDownloadCsv} data-testid="download-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Download CSV
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setDocsDrawerOpen(true)} data-testid="import-rules-btn">
            <BookOpen className="h-3.5 w-3.5 mr-1" /> Import Rules
          </Button>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/30 border rounded-md" data-testid="filters-bar">
          <form onSubmit={handleAccountSearch} className="flex items-center gap-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input ref={accountInputRef} placeholder="Account #" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="h-8 w-40 pl-7 text-xs" data-testid="filter-account-number" />
            </div>
            <Button type="submit" variant="outline" size="sm" className="h-8 text-xs px-2">Go</Button>
          </form>
          {effectiveGlobal && filterOptions?.orgs?.length > 0 && (
            <OrgCombobox orgs={filterOptions.orgs} value={filterOrg} onChange={(v) => setParam('org', v)} />
          )}
          {filterOptions?.vendors?.length > 0 && <VendorCombobox vendors={filterOptions.vendors} value={filterVendor} onChange={(v) => setParam('vendor', v)} />}
          {filterOptions?.utility_types?.length > 0 && (
            <Select value={filterType} onValueChange={(v) => setParam('utility_type', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-commodity"><SelectValue placeholder="All Commodities" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Commodities</SelectItem>{filterOptions.utility_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={filterDue || (dueWithinDays ? `due_${dueWithinDays}` : '')} onValueChange={(v) => setParam('due_filter', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-due"><SelectValue placeholder="Due Date" /></SelectTrigger>
            <SelectContent>{DUE_FILTER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterPayStatus} onValueChange={(v) => setParam('payment_status', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-pay-status"><SelectValue placeholder="Pay Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="hold">Hold</SelectItem></SelectContent>
          </Select>
          <Select value={filterRefund} onValueChange={(v) => setParam('refund_status', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-refund"><SelectValue placeholder="Refund" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Refunded</SelectItem><SelectItem value="no">Not Refunded</SelectItem></SelectContent>
          </Select>
          <Select value={filterPfStatus} onValueChange={(v) => setParam('pf_status', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-pf-status"><SelectValue placeholder="PF Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All PF</SelectItem><SelectItem value="review">Review</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
          </Select>
          {filterOptions?.paid_by_names?.length > 0 && (
            <Select value={filterPaidBy} onValueChange={(v) => setParam('paid_by', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-paid-by"><SelectValue placeholder="Paid By" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Paid By</SelectItem>
                {filterOptions.paid_by_names.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filterTxnCount} onValueChange={(v) => setParam('txn_filter', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-txn-count"><SelectValue placeholder="Transactions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transactions</SelectItem>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="multi">Multi-Payment</SelectItem>
            </SelectContent>
          </Select>
          {filterOptions?.properties?.length > 0 && (
            <Select value={filterProperty} onValueChange={(v) => setParam('property_id', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-property">
                <Home className="h-3 w-3 mr-1 opacity-60" /><SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All Properties</SelectItem>
                {filterOptions.properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.bill_count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterBillType} onValueChange={(v) => setParam('bill_type', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-bill-type">
              <DoorOpen className="h-3 w-3 mr-1 opacity-60" /><SelectValue placeholder="Units & Common Areas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Units & Common Areas</SelectItem>
              <SelectItem value="unit">Units{filterOptions?.bill_types?.unit != null ? ` (${filterOptions.bill_types.unit})` : ''}</SelectItem>
              <SelectItem value="common_area">Common Areas{filterOptions?.bill_types?.common_area != null ? ` (${filterOptions.bill_types.common_area})` : ''}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Full-width Table */}
        <div className="border rounded-md w-full overflow-x-auto">
          <Table className="w-full min-w-[1950px]">
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-muted/40 bg-card">
                {effectiveGlobal && <TableHead className="text-xs font-semibold w-[110px]">Org</TableHead>}
                <TableHead className="text-xs font-semibold w-[72px] cursor-pointer select-none" onClick={() => handleSort('due_date')} data-testid="sort-due-date">
                  <span className="flex items-center gap-1">Due <SortIcon field="due_date" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold w-[120px] cursor-pointer select-none" onClick={() => handleSort('account_number')}>
                  <span className="flex items-center gap-1">Account # <SortIcon field="account_number" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold w-[170px]">Property / Unit</TableHead>
                <TableHead className="text-xs font-semibold w-[150px] cursor-pointer select-none" onClick={() => handleSort('supplier_name')}>
                  <span className="flex items-center gap-1">Vendor <SortIcon field="supplier_name" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold w-[85px]">Commodity</TableHead>
                <TableHead className="text-xs font-semibold w-[105px]">Invoice #</TableHead>
                <TableHead className="text-xs font-semibold w-[90px] text-right">Requested</TableHead>
                <TableHead className="text-xs font-semibold w-[90px] text-right">Paid</TableHead>
                <TableHead className="text-xs font-semibold w-[70px]">Status</TableHead>
                <TableHead className="text-xs font-semibold w-[60px]">Refund</TableHead>
                <TableHead className="text-xs font-semibold w-[120px]">PF Batch</TableHead>
                <TableHead className="text-xs font-semibold w-[80px]" data-testid="col-pf-status">PF Status</TableHead>
                <TableHead className="text-xs font-semibold w-[120px]">Txn Ref</TableHead>
                <TableHead className="text-xs font-semibold w-[90px] text-center sticky right-0 z-30 bg-card shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 12 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: effectiveGlobal ? 15 : 14 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                ))
              ) : bills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={effectiveGlobal ? 15 : 14} className="text-center py-12 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />No bills found.
                  </TableCell>
                </TableRow>
              ) : bills.map((b, idx) => {
                const isDueSoon = b.due_date && b.due_date <= threeDays && b.due_date >= todayStr && b.payment_status !== 'paid';
                const isOverdue = b.due_date && b.due_date < todayStr && b.payment_status !== 'paid';
                const isSelected = (invoiceBill?.bill_id === b.bill_id && invoiceBill?.payment_file_id === b.payment_file_id)
                  || (txnBill?.bill_id === b.bill_id && txnBill?.payment_file_id === b.payment_file_id)
                  || (refundBill?.bill_id === b.bill_id && refundBill?.payment_file_id === b.payment_file_id);
                const lock = billLocks[b.bill_id];
                const lockedByOther = lock && lock.user_id !== userId;
                return (
                  <TableRow
                    key={`${b.bill_id}-${b.payment_file_id}-${idx}`}
                    className={`hover:bg-muted/50 transition-colors text-xs ${showWarnings && isOverdue ? 'bg-red-50/40' : showWarnings && isDueSoon ? 'bg-amber-50/40' : ''} ${isSelected ? 'ring-2 ring-primary/40 bg-blue-50/50' : ''}`}
                    data-testid={`bill-row-${idx}`}
                  >
                    {effectiveGlobal && <TableCell className="text-xs truncate" title={b.org_name}>{b.org_name}</TableCell>}
                    <TableCell className="text-xs whitespace-nowrap">
                      <span className="flex items-center gap-0.5">
                        {showWarnings && isOverdue && (
                          <Tooltip>
                            <TooltipTrigger><AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0" /></TooltipTrigger>
                            <TooltipContent className="text-xs"><p className="font-semibold text-red-600">Overdue</p><p>Due date was {fmtD(b.due_date)}</p></TooltipContent>
                          </Tooltip>
                        )}
                        {showWarnings && isDueSoon && !isOverdue && (
                          <Tooltip>
                            <TooltipTrigger><AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" /></TooltipTrigger>
                            <TooltipContent className="text-xs"><p className="font-semibold text-amber-600">Due Soon</p><p>Due {fmtD(b.due_date)} (within 3 days)</p></TooltipContent>
                          </Tooltip>
                        )}
                        {fmtD(b.due_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs font-mono truncate" title={b.account_number}>{b.account_number || '\u2014'}</TableCell>
                    <TableCell className="text-xs">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-1">
                          {b.bill_type === 'common_area' ? (
                            <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 px-1 py-0 h-4 font-semibold" title="Common Area bill">CA</Badge>
                          ) : b.bill_type === 'unit' ? (
                            <Badge variant="outline" className="text-[9px] bg-teal-50 text-teal-700 border-teal-200 px-1 py-0 h-4 font-semibold" title="Unit bill">Unit</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 px-1 py-0 h-4" title="Unclassified">—</Badge>
                          )}
                          {b.unit_number && (
                            <span className="text-[10px] font-mono text-muted-foreground truncate" title={`Unit ${b.unit_number}`}>#{b.unit_number}</span>
                          )}
                        </div>
                        {b.property_name && (
                          <span className="text-[10px] text-muted-foreground truncate" title={b.property_name}>{b.property_name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs truncate" title={b.supplier_name}>{b.supplier_name || '\u2014'}</TableCell>
                    <TableCell className="text-xs truncate">{b.utility_type || '\u2014'}</TableCell>
                    <TableCell className="text-xs font-mono truncate" title={b.invoice_number}>{b.invoice_number || '\u2014'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmtC(b.payment_amount || b.total_amount_due)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmtC(b.paid_amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[b.payment_status] || STATUS_COLORS.unpaid}`}>{b.payment_status || 'unpaid'}</Badge>
                    </TableCell>
                    <TableCell>
                      {b.refund === 'yes' ? (
                        <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 border-purple-200">{fmtC(b.refund_amount)}</Badge>
                      ) : <span className="text-muted-foreground text-[10px]">No</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {b.pf_batch_name ? (
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-200 truncate max-w-[110px]" title={b.pf_batch_name}>{b.pf_batch_name}</Badge>
                      ) : '\u2014'}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`pf-status-cell-${idx}`}>
                      {b.pf_status ? (
                        <Badge variant="outline" className={`text-[10px] capitalize ${PF_STATUS_COLORS[b.pf_status] || ''}`}>{b.pf_status}</Badge>
                      ) : '\u2014'}
                    </TableCell>
                    <TableCell className="text-xs font-mono truncate" title={b.transaction_reference}>{b.transaction_reference || '\u2014'}</TableCell>
                    <TableCell className="text-center sticky right-0 z-[5] bg-card shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      {lockedByOther ? (
                        <div className="flex items-center justify-center gap-1" title={`Being edited by ${lock.user_name}`}>
                          <Lock className="h-3.5 w-3.5 text-orange-500" />
                          <span className="text-[9px] text-orange-600 truncate max-w-[50px]">{lock.user_name?.split(' ')[0]}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-0.5">
                          <button onClick={() => openInvoice(b)} className="p-1 rounded hover:bg-blue-100 transition-colors" title="View Invoice" data-testid={`view-invoice-${idx}`}>
                            <File className="h-3.5 w-3.5 text-blue-600" />
                          </button>
                          {b.payment_status === 'paid' ? (
                            <button onClick={() => openPaymentDrawer(b)} className="p-1 rounded hover:bg-emerald-100 transition-colors" title="View Payment Details" data-testid={`pay-btn-${idx}`}>
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                            </button>
                          ) : (
                            <button onClick={() => openPaymentDrawer(b)} className="p-1 rounded hover:bg-emerald-100 transition-colors" title="Record Payment" data-testid={`pay-btn-${idx}`}>
                              <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                            </button>
                          )}
                          <button onClick={() => openRefundDrawer(b)} className="p-1 rounded hover:bg-red-100 transition-colors" title="Record Refund" data-testid={`refund-btn-${idx}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Load More */}
        {!loading && hasMore && bills.length < total && (
          <div className="flex justify-center py-2">
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn">
              {loadingMore ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Load More ({bills.length} of {total.toLocaleString()})
            </Button>
          </div>
        )}
      </div>

      {/* ── Invoice Panel (left slide) ── */}
      {invoiceBill && (
        <div className="fixed inset-y-0 left-0 w-1/2 bg-card border-r-2 border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-left-full duration-300" data-testid="invoice-panel">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
            <div>
              <h3 className="text-sm font-semibold">Invoice Viewer</h3>
              <p className="text-xs text-muted-foreground">{invoiceBill.supplier_name} &bull; {invoiceBill.account_number} &bull; {invoiceBill.invoice_number || invoiceBill.bill_id}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setInvoiceBill(null)} data-testid="close-invoice"><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-auto bg-muted/10">
            {pdfLoading ? <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
              : (pdfDataUrl || pdfUrl) ? <iframe src={pdfDataUrl || pdfUrl} title="Invoice PDF" className="w-full h-full border-0" />
              : <div className="flex flex-col items-center justify-center h-full text-muted-foreground"><FileText className="h-12 w-12 mb-3 opacity-40" /><p className="text-sm font-medium">{pdfMessage || 'No PDF available'}</p></div>
            }
          </div>
        </div>
      )}

      {/* ── Payment Drawer ── */}
      <Sheet open={txnDrawerOpen} onOpenChange={(o) => { if (!o) closeTxnDrawer(); }}>
        <SheetContent side="right" className="sm:max-w-[440px] w-[440px] flex flex-col p-0" data-testid="ws-txn-drawer">
          <SheetHeader className="px-6 pt-6 pb-3">
            <SheetTitle className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4" />
              {txnForm._locked && !showAddTxnForm ? 'Payment Details' : 'Record Payment'}
            </SheetTitle>
            {txnBill && (
              <div className="text-xs text-muted-foreground space-y-0.5 pt-1">
                <p><strong>{txnBill.supplier_name}</strong> &bull; {txnBill.account_number}</p>
                <p>Invoice: {txnBill.invoice_number || '\u2014'} &bull; Requested: {fmtC(txnBill.payment_amount || txnBill.total_amount_due)}</p>
                <p>PF: {txnBill.pf_batch_name || '\u2014'}{paymentSummary?.pf_status ? ` (${paymentSummary.pf_status})` : ''}</p>
                {paymentSummary && (paymentSummary.property_name || paymentSummary.unit_number) && (
                  <p>Property: {paymentSummary.property_name || '\u2014'}{paymentSummary.unit_number ? ` · Unit ${paymentSummary.unit_number}` : ''}</p>
                )}
              </div>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Status Summary Banner */}
            {paymentSummary && (
              <div className="border rounded-lg p-3 bg-muted/20 text-xs space-y-1.5" data-testid="ws-payment-summary">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Stored Status</span>
                  <Badge variant="outline" className={STATUS_COLORS[paymentSummary.stored_status] || ''}>{paymentSummary.stored_status}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Computed (from {paymentSummary.transaction_count} txn)</span>
                  <Badge variant="outline" className={STATUS_COLORS[paymentSummary.computed_status] || 'bg-slate-100 text-slate-600 border-slate-200'}>{paymentSummary.computed_status}</Badge>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Paid: <span className="font-mono font-semibold text-emerald-700">{fmtC(paymentSummary.txn_sum)}</span> / {fmtC(paymentSummary.payment_amount)}</span>
                  {paymentSummary.remaining > 0.01 && <span>Remaining: <span className="font-mono text-amber-700">{fmtC(paymentSummary.remaining)}</span></span>}
                </div>
                {paymentSummary.status_mismatch && (
                  <div className="flex items-start gap-1.5 text-[11px] text-amber-700 pt-1 border-t">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>Stored status disagrees with transaction history. Consider running reconciliation.</span>
                  </div>
                )}
              </div>
            )}

            {/* Payment Selection editor — only shown for bills inside a REVIEW PF */}
            {paymentSummary?.pf_status === 'review' && (
              <div className="border rounded-lg p-3 bg-blue-50/40 text-xs space-y-2" data-testid="ws-payment-selection-edit">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-700 uppercase text-[10px] tracking-wider">Payment Selection (Review)</span>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Editable</Badge>
                </div>
                <Select
                  value={paymentSummary.payment_selection || 'New Charges'}
                  onValueChange={async (v) => {
                    if (v === paymentSummary.payment_selection) return;
                    setSavingSelection(true);
                    try {
                      // Need utility_id — look up
                      const utilRes = await api.get(`/payments/v2/files/${txnBill.payment_file_id}/utilities`, {
                        params: { account_id: txnBill.account_id, account_number: txnBill.account_number, limit: 10 },
                      });
                      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === txnBill.bill_id);
                      if (!matchUtil) { toast.error('Utility record not found'); return; }
                      await api.patch('/payments/v2/row-edit', {
                        payment_file_id: txnBill.payment_file_id,
                        account_id: txnBill.account_id,
                        utility_id: matchUtil.payment_file_utility_id,
                        field_name: 'payment_selection',
                        new_value: v,
                        user_id: userId, user_name: userName,
                        user_role: user?.role || '',
                        row_number: 0,
                      });
                      toast.success(`Payment selection updated to "${v}"`);
                      // Refresh summary
                      const { data: refreshed } = await api.get('/payments/v2/workspace/bill-payment-summary', {
                        params: { bill_id: txnBill.bill_id, payment_file_id: txnBill.payment_file_id, account_id: txnBill.account_id },
                      });
                      setPaymentSummary(refreshed);
                      loadBills();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to update selection');
                    } finally {
                      setSavingSelection(false);
                    }
                  }}
                  disabled={savingSelection}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="ws-payment-selection-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New Charges">New Charges ({fmtC(paymentSummary.current_bill_amount)})</SelectItem>
                    <SelectItem value="Total Amount Due">Total Amount Due ({fmtC(paymentSummary.total_amount_due)})</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Changing selection will recalculate this bill&apos;s requested amount. All actions are audited.
                </p>
              </div>
            )}

            {/* LOCKED VIEW */}
            {txnForm._locked && !showAddTxnForm && (
              <div className="space-y-4" data-testid="ws-txn-locked-view">
                <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Transaction Reference</label>
                    <p className="text-sm font-mono font-medium mt-0.5">{txnForm.transaction_reference || '\u2014'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Paid Amount</label>
                      <p className="text-sm font-mono font-bold text-emerald-700 mt-0.5">{fmtC(txnForm.amount)}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Service Fee</label>
                      <p className="text-sm font-mono mt-0.5">{txnForm.payment_service_charge ? fmtC(txnForm.payment_service_charge) : '\u2014'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Payment Method</label>
                      <p className="text-sm mt-0.5 capitalize">{txnForm.payment_method || '\u2014'}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Transaction Date</label>
                      <p className="text-sm mt-0.5">{txnForm.transaction_date ? fmtD(txnForm.transaction_date) : '\u2014'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Paid By</label>
                    <p className="text-sm font-medium mt-0.5">{txnForm.paid_by_name || '\u2014'}</p>
                  </div>
                  {txnForm.notes && (
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Notes</label>
                      <p className="text-sm text-muted-foreground mt-0.5">{txnForm.notes}</p>
                    </div>
                  )}
                </div>
                {billTxnsLoading ? (
                  <div className="flex items-center justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                ) : billTxns.length > 1 ? (
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Transaction History ({billTxns.length})</p>
                    {billTxns.map((t, i) => (
                      <div key={t.id || i} className="border border-border rounded p-2.5 bg-background text-xs flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-muted-foreground font-mono text-[10px]">#{i + 1}</span>
                          <span className="font-bold font-mono text-emerald-700">{fmtC(t.amount)}</span>
                          <span className="text-muted-foreground truncate" title={t.transaction_reference}>{t.transaction_reference || '\u2014'}</span>
                        </div>
                        <span className="text-muted-foreground shrink-0">{t.paid_by_name || '\u2014'}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* EDITABLE FORM */}
            {showAddTxnForm && (
              <div className="space-y-4" data-testid="ws-add-txn-form">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Transaction Reference *</label>
                  <Input className="h-9 text-sm font-mono" value={txnForm.transaction_reference || ''} onChange={e => setTxnForm(prev => ({ ...prev, transaction_reference: e.target.value }))} placeholder="e.g. CHK-2026-001" data-testid="ws-txn-ref" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Paid Amount *</label>
                    <Input type="number" className="h-9 text-sm font-mono" value={txnForm.amount || ''} onChange={e => setTxnForm(prev => ({ ...prev, amount: e.target.value }))} step="0.01" data-testid="ws-txn-amount" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Service Fee</label>
                    <Input type="number" className="h-9 text-sm font-mono" value={txnForm.payment_service_charge || ''} onChange={e => setTxnForm(prev => ({ ...prev, payment_service_charge: e.target.value }))} step="0.01" placeholder="0.00" data-testid="ws-txn-svc" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Payment Method</label>
                    <Select value={txnForm.payment_method || ''} onValueChange={v => setTxnForm(prev => ({ ...prev, payment_method: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="ws-txn-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="ach">ACH</SelectItem>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Transaction Date</label>
                    <Input type="date" className="h-9 text-sm" value={txnForm.transaction_date || ''} onChange={e => setTxnForm(prev => ({ ...prev, transaction_date: e.target.value }))} data-testid="ws-txn-date" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Paid By *</label>
                  <Select value={txnForm.paid_by_id || userId} onValueChange={v => {
                    const u = orgUsers.find(ou => ou.id === v);
                    setTxnForm(prev => ({ ...prev, paid_by_id: v, paid_by_name: u?.name || prev.paid_by_name }));
                  }}>
                    <SelectTrigger className="h-9 text-sm" data-testid="ws-txn-paid-by"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      {orgUsers.length === 0 && <SelectItem value={userId}>{userName} (current)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Notes</label>
                  <textarea className="w-full min-h-[70px] p-2 text-sm border border-input bg-background rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-ring" value={txnForm.notes || ''} onChange={e => setTxnForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Payment notes..." data-testid="ws-txn-notes" />
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border px-6 py-3 flex items-center gap-2 justify-between">
            <div>
              {txnForm._locked && !showAddTxnForm && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => { setShowAddTxnForm(true); setTxnForm(prev => ({ ...prev, _locked: false, paid_by_id: prev.paid_by || userId })); }} data-testid="ws-edit-txn-btn">
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={closeTxnDrawer}>Close</Button>
              {showAddTxnForm && (
                <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveTxn} disabled={txnSaving} data-testid="ws-save-txn">
                  {txnSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                  Save Payment
                </Button>
              )}
              {txnForm._locked && !showAddTxnForm && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                  setShowAddTxnForm(true);
                  const totalReq = parseFloat(txnBill?.payment_amount || txnBill?.total_amount_due || 0);
                  const existingPaid = parseFloat(txnForm.amount || 0);
                  const rem = Math.max(0, totalReq - existingPaid);
                  setTxnForm({ transaction_reference: '', amount: rem > 0 ? rem : '', payment_service_charge: '', payment_method: '', notes: '', transaction_date: new Date().toISOString().split('T')[0], _totalRequested: totalReq, paid_by_id: userId, paid_by_name: userName, _locked: false });
                  setEditingTxnId(null);
                }} data-testid="ws-add-another-txn-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Transaction
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Refund Drawer ── */}
      <Sheet open={refundDrawerOpen} onOpenChange={(o) => { if (!o) closeRefundDrawer(); }}>
        <SheetContent side="right" className="sm:max-w-[420px] w-[420px] flex flex-col p-0" data-testid="ws-refund-drawer">
          <SheetHeader className="px-6 py-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-base"><ArrowRightLeft className="h-4 w-4 text-red-600" />{refundForm.isEdit ? 'Edit' : 'Record'} Refund</SheetTitle>
            {refundBill && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>{refundBill.supplier_name} &bull; {refundBill.account_number}</p>
                <p>Paid Amount: <strong className="text-emerald-700">{fmtC(refundBill.paid_amount)}</strong></p>
              </div>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refund Amount *</label>
              <Input type="number" className="h-9 text-sm font-mono" value={refundForm.refund_amount || ''} onChange={e => setRefundForm(prev => ({ ...prev, refund_amount: e.target.value }))} step="0.01" placeholder="0.00" data-testid="ws-refund-amount" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refunded By</label>
              <Select value={refundForm.refunded_by_id || userId} onValueChange={v => {
                const u = orgUsers.find(ou => ou.id === v);
                setRefundForm(prev => ({ ...prev, refunded_by_id: v, refunded_by: u?.name || prev.refunded_by }));
              }}>
                <SelectTrigger className="h-9 text-sm" data-testid="ws-refund-by-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3rd_party">3rd Party Payment</SelectItem>
                  {orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refund Date</label>
              <Input type="date" className="h-9 text-sm" value={refundForm.refund_date ? refundForm.refund_date.split('T')[0] : ''} onChange={e => setRefundForm(prev => ({ ...prev, refund_date: e.target.value }))} data-testid="ws-refund-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refund Method / Notes</label>
              <textarea className="w-full min-h-[60px] p-2 text-sm border border-input bg-background rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-ring" value={refundForm.refund_method || ''} onChange={e => setRefundForm(prev => ({ ...prev, refund_method: e.target.value }))} placeholder="Credit note, ACH reversal..." data-testid="ws-refund-method" />
            </div>
            <div className="bg-red-50/50 border border-red-200 p-3 space-y-1.5 text-xs rounded-md">
              <p className="font-medium text-red-700 uppercase text-[10px] tracking-wider">Record Details</p>
              <div className="flex justify-between"><span className="text-muted-foreground">Refunded By</span><span className="font-medium">{refundForm.refunded_by || '3rd Party Payment'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Recorded By</span><span className="font-medium">{isSuper ? 'System' : userName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium">{refundForm.refund_date ? new Date(refundForm.refund_date).toLocaleDateString() : new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</span></div>
            </div>
          </div>
          <div className="border-t border-border px-6 py-4 flex items-center gap-2 justify-between">
            <div>{refundForm.isEdit && <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={clearRefund} disabled={refundSaving} data-testid="ws-clear-refund"><Trash2 className="h-3 w-3 mr-1" />Clear</Button>}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={closeRefundDrawer}>Cancel</Button>
              <Button size="sm" className="text-xs bg-red-600 hover:bg-red-700 text-white" onClick={saveRefund} disabled={refundSaving} data-testid="ws-save-refund">
                {refundSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowRightLeft className="h-3 w-3 mr-1" />}
                {refundForm.isEdit ? 'Update' : 'Record'} Refund
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Import Rules & Exceptions Documentation Drawer */}
      <Sheet open={docsDrawerOpen} onOpenChange={setDocsDrawerOpen}>
        <SheetContent side="right" className="sm:max-w-[50%] w-[50%] flex flex-col p-0" data-testid="import-rules-drawer">
          <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-sm"><BookOpen className="h-4 w-4" />Transaction Import Rules</SheetTitle>
            <p className="text-xs text-muted-foreground">V15 import logic, payment assignment rules, service charge policy</p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 text-sm">
            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Rule 1: BillPortal Payments (Col AA)</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>If <strong>"Paid by BillPortal ($)"</strong> &gt; $0 for a Bill ID: mark as <strong>paid</strong></li>
                <li>Set paid_by_name = <strong>"BillPortal"</strong></li>
                <li>Record: transaction reference (Col X), service charge (Col W), paid date (Col Z), notes (Col AE + AC)</li>
                <li>For same bill_id split by commodity (multiple CSV rows): use the row with &gt; $0</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Rule 2: Both Zero &mdash; Backlog</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>If <strong>both</strong> "Paid by BillPortal" = $0 AND "Paid by Anchor" = $0: <strong>Skip</strong></li>
                <li>Do not import &mdash; backlog for investigation (3,029 rows in V15)</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Rule 3: Anchor Payments (Col AB)</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>If <strong>"Paid by Anchor ($)"</strong> &gt; $0: add Anchor transaction</li>
                <li>Both BillPortal and Anchor CAN exist on same bill (dual payment)</li>
                <li>Use payment date and notes from CSV</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Rule 3.1: Service Charge Policy</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>If <strong>only Anchor</strong> paid (BP = $0) and has service charge: <strong>DO NOT include</strong> service charge</li>
                <li>Service charge is <strong>ONLY</strong> recorded when BillPortal paid &gt; $0</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Stats Display Logic</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li><strong>Total Paid</strong> = Sum of bill-level paid_amount (BP + Anchor combined)</li>
                <li><strong>Paid by BP</strong> = Sum from <code className="bg-muted px-1 rounded">pm_payment_file_transactions</code> where paid_by IN ("BillPortal", "Manoj")</li>
                <li><strong>Paid by Others</strong> = Sum from transactions where paid_by NOT IN ("BillPortal", "Manoj")</li>
                <li><strong>Service Fees</strong> = Sum of payment_service_charge on bills (only when BP paid)</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Import History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b"><th className="text-left py-1 pr-3">Date</th><th className="text-left py-1 pr-3">File</th><th className="text-left py-1 pr-3">Bills Updated</th><th className="text-left py-1">Txns Created</th></tr></thead>
                  <tbody>
                    <tr className="border-b border-border/50"><td className="py-1 pr-3">2026-04-30</td><td className="py-1 pr-3">Billportal-v15.csv</td><td className="py-1 pr-3">6,831</td><td className="py-1">6,893</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">V15 Source Columns</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>Col A: Bill ID</li>
                <li>Col D: Customer</li>
                <li>Col W (22): Service Fee</li>
                <li>Col X (23): Transaction Ref No</li>
                <li>Col Z (25): Payment Date</li>
                <li>Col AA (26): <strong>Paid by BillPortal ($)</strong></li>
                <li>Col AB (27): <strong>Paid by Anchor ($)</strong></li>
                <li>Col AC (28): Comments</li>
                <li>Col AE (30): Notes</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2 text-sm border-b border-border pb-1">Backlog / To Investigate</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>Bills where both BP=$0 and Anchor=$0 (3,029 rows in V15)</li>
                <li>2 bill_ids from V15 not found in database</li>
                <li>$0 Paid by BillPortal AND $0 Paid by Anchor scenarios &mdash; future investigation</li>
              </ul>
            </section>
          </div>
          <div className="border-t border-border px-6 py-3 flex justify-end">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setDocsDrawerOpen(false)}>Close</Button>
          </div>
        </SheetContent>
      </Sheet>

    </TooltipProvider>
  );

  return noLayout ? inner : <MainLayout wide>{inner}</MainLayout>;
}
